import { DatePipe, DOCUMENT } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Account } from '@df/account/account.interfaces';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { LocalStorage } from '@df/browser/storage/decorator/local-storage';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { PRODUCT_VIEW_MODEL_CHANGE_EVENT_NAME } from '@df/catalog-ui/product/view/product-view.component.def';
import { Category, type ICategoryDataV2, ICategoryDataV3 } from '@df/catalog/category/category';
import { IPage, IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { ICrumb } from '@df/catalog/model';
import { type IProductDataV2, type IProductDataV3, Product } from '@df/catalog/product/product';
import { Stock } from '@df/catalog/stock/stock';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { NPrivacyState, PrivacyStateService } from '@df/core/meta/privacy-state.service';
import { routeOverride } from '@df/core/meta/route-override.pipe';
import { Status, STATUS_SUCCESS } from '@df/core/status';
import { Braintree } from '@df/module-braintree/braintree.namespace';
import { GtmService } from '@df/module-gtm/gtm.service';
import { ProcessorService } from '@df/ng/processor.service';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { SearchResult } from '@df/search/search-result';
import { SearchResultEvent } from '@df/search/search-result-event';
import { SearchService } from '@df/search/search.service';
import { Basket, type IBasketData } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import {
    BasketService,
    IAddToBasketSuccessEventData,
    IChangeQtyBasketItemSuccessEventData,
    IRemoveFromBasketSuccessEventData
} from '@df/session/api/basket/basket.service';
import { CustomerModel } from '@df/session/api/customer.model';
import { IAddToWishlistData, WISHLIST_ADD_SUCCESS_EVENT, WISHLIST_REMOVE_SUCCESS_EVENT } from '@df/session/api/wishlist/wishlist';
import { NewsletterChangeEvent } from '@df/session/newsletter/newsletter-change.event';
import { NewsletterUpdateEvent } from '@df/session/newsletter/newsletter-update.event';
import { Newsletter } from '@df/session/newsletter/newsletter.interfaces';
import { NewsletterService } from '@df/session/newsletter/newsletter.service';
import { NOTICES } from '@df/ui/api/notice/notice.const';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { toDate } from '@df/util/fn/to-date';
import cloneDeep from 'lodash-es/cloneDeep';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import map from 'lodash-es/map';
import pickBy from 'lodash-es/pickBy';
import startsWith from 'lodash-es/startsWith';
import take from 'lodash-es/take';
import { Subscription } from 'rxjs';
import { ProductDecorator } from '../../catalog/model/product/product.decorator';
import { CheckoutFormComponent } from '../../checkout/checkout-form.component';
import { bloomreachSdkLoader } from './bloomreach-sdk-loader';
import { NBloomreach } from './bloomreach.namespace';

@Injectable({
    providedIn: 'root'
})
export class BloomreachService implements OnDestroy {
    protected initiated = false;
    protected sdkLoaded = false;
    protected initSdkAttempts = 0;
    protected initAttempts = 0;

    /**
     * Events list waiting Bloomreach to be initiated
     */
    private waitingEvents: NBloomreach.IEvent[] = [];

    private privacyServiceSubscription?: Subscription;
    private subscriptions: Subscription = new Subscription();

    private lastPath?: string;
    private datePipe?: DatePipe;

    /**
     * Currently added coupon model
     */
    protected _promo: NBloomreach.ICurrentPromo = { code: null, value: 0 };

    expressPaymentMethods = [Braintree.Methods.ApplePay, Braintree.Methods.GooglePay] as string[];

    @LocalStorage('checkoutBeforeSubmitData')
    private checkoutBeforeSubmitData: NBloomreach.ICheckoutSubmitData | undefined;

    private readonly app = inject(App);
    private readonly timeoutService = injectTimeoutService();
    private readonly privacyStateService = inject(PrivacyStateService);
    private readonly basketService = inject(BasketService);
    private readonly searchService = inject(SearchService);
    private readonly newsletterService = inject(NewsletterService);
    private readonly customer = inject(CustomerModel);
    private readonly meta = inject(Meta);
    private readonly basket = inject(Basket);
    private readonly router = inject(Router);
    private readonly gtmService = inject(GtmService);
    private readonly requestService = inject(RequestService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly window = inject(DOCUMENT).defaultView as Window & typeof globalThis;

    private searchEventDebounced = this.timeoutService.debounce((search?: SearchResult) => this.searchEvent(search), 1000);
    protected onMetaChangeDebounced = this.timeoutService.debounce(() => this.onMetaChange(), 500);

    constructor() {
        this.app.apiConfigService.loadApiConfig().then(() => {
            this.datePipe = new DatePipe(this.app.data.locale || 'en-GB');
        });

        // Set listeners
        this.setListeners();
    }

    /**
     * Current promo setter
     */
    protected set promo(promo: NBloomreach.ICurrentPromo) {
        const previousPromo = this.promo;
        this._promo = promo;

        if (previousPromo.code !== this.promo.code) {
            const action = this.promo.code ? NBloomreach.ECartUpdateActions.PromoAdd : NBloomreach.ECartUpdateActions.PromoRemove;
            const changedPromo = (promo.code || previousPromo.code) as string;
            const changedValue = promo.value || previousPromo.value;
            this.onPromoChange(action, changedPromo, changedValue);
        }
    }

    /**
     * Current promo getter
     */
    protected get promo(): NBloomreach.ICurrentPromo {
        return this._promo;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();

        if (typeof window !== 'undefined') {
            window.removeEventListener('OneTrustGroupsUpdated', () => this.onPrivacySettingsChange());
        }
    }

    /**
     * Load SDK without initialization - SDK will be ready but not started
     */
    appInitialize() {
        if (typeof window === 'undefined') {
            return;
        }

        // Listen for OneTrust groups updated event
        window.addEventListener('OneTrustGroupsUpdated', () => this.onPrivacySettingsChange());

        // Load SDK immediately without waiting for consent
        this.loadSDK();
    }

    protected loadSDK() {
        const config = this.app.dfConfig.bloomreach;

        if (config?.token) {
            try {
                // Use inline loader to load SDK without starting it
                bloomreachSdkLoader(config);

                const onSdkLoaded = () => {
                    if (window.exponea && !this.sdkLoaded) {
                        this.onBloomreachSdkLoaded();
                    } else if (this.initSdkAttempts < 20) {
                        this.initSdkAttempts++;
                        this.timeoutService.setTimeout(() => onSdkLoaded(), 500);
                    } else {
                        this.initSdkAttempts = 0;
                    }
                };

                onSdkLoaded();
            } catch (error) {
                this.app.logger.error('Error loading Bloomreach SDK', error);
            }
        }
    }

    protected setListeners() {
        this.subscriptions.add(
            this.basketService.subscribeToAddToBasketSuccess((eventData: IAddToBasketSuccessEventData) => this.onBasketAdd(eventData))
        );

        this.subscriptions.add(
            this.basketService.subscribeToRemoveFromBasketSuccess((eventData: IRemoveFromBasketSuccessEventData) =>
                this.onBasketRemove(eventData)
            )
        );

        this.subscriptions.add(
            this.basketService.subscribeToChangeQtySuccess((eventData: IChangeQtyBasketItemSuccessEventData) =>
                this.onBasketItemQtyChange(eventData)
            )
        );

        this.subscriptions.add(
            this.searchService.subscribeToSearch((searchEvent: SearchResultEvent) => this.searchEventDebounced(searchEvent.result))
        );

        this.subscriptions.add(
            this.newsletterService.newsletterStatus.subscribe(event => {
                const subscribeRequest = event.data as Newsletter.ISubscribeRequest;
                const placement = subscribeRequest?.source;

                if (event instanceof NewsletterChangeEvent) {
                    this.onNewsletterConsentChange(event.subscribed, event.email || this.customer.data.email, placement);
                } else if (event instanceof NewsletterUpdateEvent) {
                    this.onNewsletterConsentChange(this.customer.data.newsletter, this.customer.data.email, placement);
                }
            })
        );

        this.subscriptions.add(
            this.app.events
                .on(WISHLIST_ADD_SUCCESS_EVENT)
                .subscribe((data: IAddToWishlistData) =>
                    this.onWishlistChange(NBloomreach.EWishlistActions.Add, Array.isArray(data.product) ? data.product : [data.product])
                )
        );

        this.subscriptions.add(
            this.app.events.on(WISHLIST_REMOVE_SUCCESS_EVENT).subscribe((productId: number | number[]) => {
                productId = Array.isArray(productId) ? productId : [productId];
                this.onWishlistChange(NBloomreach.EWishlistActions.Remove, productId);
            })
        );

        this.subscriptions.add(
            this.app.events
                .on(Checkout.EVENTS.StepChange)
                .subscribe((data: Checkout.IStepChangeData) =>
                    this.onCheckoutStepChange(data.checkout as CheckoutFormComponent, data.stepBeforeChange)
                )
        );

        this.subscriptions.add(
            this.app.event.success(event => {
                switch (event.id) {
                    case 'nexusCustomerRegister':
                        this.onRegister();
                        break;
                    case 'nexusCustomerLogin':
                    case 'nexusCheckoutLogin':
                        this.onLogin();
                        break;
                }
            })
        );

        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.SuccessTracking).subscribe((data: Checkout.ISuccessData) => this.onCheckoutSuccess(data))
        );

        this.subscriptions.add(
            this.app.events.on(Account.CUSTOMER_DATA_UPDATE_EVENT).subscribe(() => {
                this.updateCustomerData();
                this.identifyCustomer();
            })
        );

        this.subscriptions.add(
            this.app.events
                .on(Checkout.EVENTS.SubmitBefore)
                .subscribe((checkoutSubmitBeforeData: Checkout.ISubmitBeforeEventData) =>
                    this.onCheckoutSubmitBefore(checkoutSubmitBeforeData)
                )
        );

        this.subscriptions.add(this.app.events.on(Checkout.EVENTS.SubmitError).subscribe(() => this.onCheckoutError()));

        this.subscriptions.add(this.basket.subscribe(() => this.onBasketUpdate()));
        this.customer.load().then(() => {
            this.onAuthentication();
            this.subscriptions.add(this.customer.obs.status.subscribe(() => this.onAuthentication()));
        });

        this.subscriptions.add(this.meta.subscribe(() => this.onMetaChangeDebounced()));

        this.subscriptions.add(
            this.app.events.on(PRODUCT_VIEW_MODEL_CHANGE_EVENT_NAME).subscribe((product: ProductDecorator<IProductDataV3>) => {
                this.viewProductEvent(product);
            })
        );

        if (this.app.isPlatformBrowser) {
            this.subscriptions.add(
                this.privacyStateService.selection$.subscribe(() => {
                    if (window.exponea && this.initiated) {
                        const isAllowed = this.privacyStateService.getState(NPrivacyState.Area.personalization).value;
                        window.exponea.update({ cookies_accepted: isAllowed ? 'yes' : 'no' });
                    }
                })
            );
        }

        // kill newsletter subscription; pass to further processing
        this.subscriptions.add(
            this.app.event.before(event => {
                switch (event.id) {
                    case 'nexusCustomerLogin':
                    case 'nexusCheckoutLogin':
                    case 'nexusCustomerRegister': {
                        const data = event.data;
                        if ('newsletter' in data) {
                            event['newsletterBeforeClean'] = data.newsletter;
                            event['placement'] = data['placement'];
                            delete data.newsletter;
                            delete data['placement'];
                        }
                        break;
                    }
                }
            })
        );
        // read passed param and call Bloomreach directly
        this.subscriptions.add(
            this.app.event.success(event => {
                switch (event.id) {
                    case 'nexusCustomerLogin':
                    case 'nexusCheckoutLogin':
                    case 'nexusCustomerRegister':
                        if ('newsletterBeforeClean' in event && 'placement' in event && event.newsletterBeforeClean !== undefined) {
                            this.onLoginOrRegisterRequest(!!event.newsletterBeforeClean, event.placement as string | undefined);
                        }
                        break;
                }
            })
        );

        // replace form subscription with another request
        this.subscriptions.add(
            this.app.events.on<Newsletter.ISubscriptionEvent>(Newsletter.subscriptionRequest).subscribe(event => {
                event.callback = data =>
                    new Promise((resolve, reject) => {
                        this.checkConsent(undefined, data.email, result => {
                            if (!result?.consent) {
                                resolve({
                                    success: STATUS_SUCCESS
                                });
                            } else {
                                reject({
                                    error: NOTICES['SUBSCRIBED']
                                });
                            }
                        });
                    }) as Promise<any>;
            })
        );
    }

    protected onBloomreachSdkLoaded() {
        // On Bloomreach SDK loaded
        this.sdkLoaded = true;
        this.app.logger.log('Bloomreach SDK loaded (not started)');

        // Subscribe to privacy selection changes to start tracking after consent
        const privacyStateService = ProcessorService.injector.get(PrivacyStateService);
        this.privacyServiceSubscription = privacyStateService.selection$.subscribe(() => {
            if (this.canStartTracking(privacyStateService)) {
                this.startTracking();
                this.privacyServiceSubscription?.unsubscribe();
            }
        });
    }

    protected canStartTracking(privacyStateService: PrivacyStateService): boolean {
        return (
            (privacyStateService.getState(NPrivacyState.Area.personalization).value ||
                privacyStateService.getState(NPrivacyState.Area.performance).value ||
                privacyStateService.getState(NPrivacyState.Area.advertising).value) &&
            !this.initiated &&
            this.sdkLoaded
        );
    }

    protected startTracking() {
        // Start tracking
        this.app.logger.log('Starting Bloomreach tracking');
        if (typeof window === 'undefined') {
            return;
        }

        const config = this.app.dfConfig.bloomreach;

        if (!this.initiated && config?.token) {
            const onInit = () => {
                if (this.sdkLoaded && window.exponea?.start) {
                    // Call the start function that was set up by the SDK loader
                    window.exponea.start();

                    this.initiated = true;

                    this.waitingEvents.forEach(event => this.track(event));
                    this.waitingEvents = [];
                }
            };

            if (this.sdkLoaded && window.exponea && config.track && config.track.google_analytics === false) {
                onInit();
            } else if (window.exponea && window['ga'] && window['ga'].loaded) {
                onInit();
            } else if (this.initAttempts < 20) {
                this.initAttempts++;
                this.timeoutService.setTimeout(() => this.startTracking(), 500);
            } else {
                this.initAttempts = 0;
            }
        }
    }

    track(event: NBloomreach.IEvent): void {
        if (typeof window === 'undefined') {
            return;
        }

        this.app.logger.log('Bloomreach event track->before:', event);

        if (this.canTrack(event)) {
            this.app.logger.info('Bloomreach event track:', event);
            window.exponea.track(
                event.name,
                pickBy(event.properties, value => value !== undefined && value !== '')
            );
        } else {
            this.app.logger.warn('Bloomreach event track->waiting:', event);
            this.waitingEvents.push(event);
        }
    }

    trackBackend(event: NBloomreach.IBackendEvent): void {
        if (typeof window === 'undefined') {
            return;
        }

        this.requestService.send(
            undefined,
            { path: NBloomreach.ENDPOINT.BULK, data: event },
            response => {
                this.app.logger.info('Bloomreach event trackBackend->response', response);
            },
            error => {
                this.app.logger.error('Bloomreach event trackBackend->error', error);
            }
        );
    }

    protected canTrack(event: NBloomreach.IEvent): boolean {
        // C0001 Strictly Necessary Cookies
        // C0002 Performance & Analytical Cookies
        // C0003 Functional Cookies
        // C0004 Targeting Cookies
        // C0005 Social Media Cookies
        const area: Record<string, string[]> = {
            // strictly necessary (C0001 Strictly Necessary Cookies)
            core: [
                NBloomreach.EEvent.Authentication,
                NBloomreach.EEvent.NewsletterConsent,
                NBloomreach.EEvent.Purchase,
                NBloomreach.EEvent.PurchaseItem,
                NBloomreach.EEvent.StockNotification
            ],
            // performance and analytics (C0002 Performance & Analytical Cookies)
            performance: [
                NBloomreach.EEvent.ViewProduct,
                NBloomreach.EEvent.ViewCategory,
                NBloomreach.EEvent.CartUpdate,
                NBloomreach.EEvent.Wishlist
            ],
            // functional (C0003 Functional Cookies)
            personalization: [NBloomreach.EEvent.Checkout, NBloomreach.EEvent.Search],
            // targeting (C0004 Targeting Cookies)
            advertising: []
        };

        return (
            this.initiated &&
            ((this.privacyStateService.getState(NPrivacyState.Area.core).value && area['core'].includes(event.name)) ||
                (this.privacyStateService.getState(NPrivacyState.Area.personalization).value &&
                    area['personalization'].includes(event.name)) ||
                (this.privacyStateService.getState(NPrivacyState.Area.performance).value && area['performance'].includes(event.name)) ||
                (this.privacyStateService.getState(NPrivacyState.Area.advertising).value && area['advertising'].includes(event.name)))
        );
    }

    /**
     * On add to basket callback
     */
    protected onBasketAdd(requestData: IAddToBasketSuccessEventData): void {
        const basketSubscription = this.basket.subscribe(() => {
            basketSubscription.unsubscribe();
            const addRequests = requestData.request.products;

            addRequests.forEach(request => {
                const basketItem = this.basket.byAddToBasketRequest(request);

                if (basketItem) {
                    this.onBasketItemsChange(NBloomreach.ECartUpdateActions.Add, basketItem);
                }
            });
        });
    }

    /**
     * On remove from basket callback
     */
    protected onBasketRemove(requestData: IRemoveFromBasketSuccessEventData): void {
        const basketItems = requestData.items;

        if (basketItems?.length) {
            basketItems.forEach(basketItem => this.onBasketItemsChange(NBloomreach.ECartUpdateActions.Remove, basketItem));
        }
    }

    /**
     * On basket item qty change callback
     */
    protected onBasketItemQtyChange(requestData: IChangeQtyBasketItemSuccessEventData): void {
        const id = requestData.request.id;

        const basketSubscription = this.basket.subscribe(() => {
            basketSubscription.unsubscribe();
            const basketItem = this.basket.byId(id);

            if (basketItem) {
                this.onBasketItemsChange(NBloomreach.ECartUpdateActions.Quantity, basketItem);
            }
        });
    }

    /**
     * On basket items change(add, remove, qty change) callback
     */
    protected onBasketItemsChange(action: NBloomreach.TItemsCartUpdateAction, basketItem: BasketItem): void {
        const eventProperties = this.getBasketItemEventProperties(action, basketItem);

        if (eventProperties) {
            this.cartUpdateEvent(eventProperties);
        }
    }

    /**
     * Returns event properties for basket items cart update event
     */
    protected getBasketItemEventProperties(
        action: NBloomreach.TItemsCartUpdateAction,
        basketItem: BasketItem
    ): NBloomreach.IBasketItemCartUpdateEventProperties | undefined {
        const product = basketItem.product as ProductDecorator<IProductDataV3>;

        if (product?.data) {
            const price = basketItem.price;
            const orgPrice = basketItem.orgPrice;
            const discount = orgPrice && price && orgPrice > price ? orgPrice - price : 0;

            const { coupon, coupon_value } = this.basket.data;

            const eventProperties: NBloomreach.IBasketItemCartUpdateEventProperties = {
                ...this.getCartUpdateEventProperties(action),
                ...this.getProductBloomreachEventProperties(product),
                price_local_currency: price,
                discount_value: discount,
                original_price: orgPrice || price,
                original_price_local_currency: orgPrice || price,
                local_currency: this.app.data.currency.base,
                promo_code: coupon ? (Array.isArray(coupon) ? coupon[0] : coupon) : undefined,
                promo_value: coupon_value,
                ...this.getLocalProductEventProperties(product),
                ...this.getLocalBasketEventProperties(),
                ...this.getGeneralProperties()
            };

            return eventProperties;
        }

        return undefined;
    }

    protected getLocalBasketEventProperties(): NBloomreach.IBasketEventProperties {
        const grandTotal = this.basket.data.totals?.grand_total || 0;
        const tax = this.basket.data.totals?.tax || 0;

        return {
            total_price: (this.basket.data.totals && grandTotal * this.getPriceGBPRate()) || 0,
            total_price_without_tax: Math.round((grandTotal - tax) * this.getPriceGBPRate() * 100) / 100 || 0,
            quote_id: this.basket.data.quote_id?.toString() || '',
            cart_hash: this.basket.data.cart_hash || '',
            ...this.getProductAndVariantLists()
        };
    }

    protected getProductAndVariantLists(): NBloomreach.IProductAndVariantLists {
        const productList = map(this.basket.data.items, item => {
            const productId = item.product;

            if (productId) {
                const product = this.app.resources.product.getExisting(productId);

                if (product && product.parent && product.parent.data) {
                    const sku = product.parent.data.sku;

                    if (sku) {
                        return {
                            product_id: sku,
                            quantity: item.request.qty
                        };
                    }
                }
            }
            return undefined;
        });

        const productIds = map(productList, item => item && item.product_id);
        const variantList = map(this.basket.data.items, item => {
            const productId = item.product;

            if (productId) {
                const product = this.app.resources.product.getExisting(productId);

                if (product) {
                    const parent = product.parent;

                    if (product.data && parent.data && product.data.sku !== parent.data.sku) {
                        const sku = product.data.sku;

                        if (sku) {
                            return {
                                product_id: sku,
                                quantity: item.request.qty
                            };
                        }
                    }
                }
            }

            return undefined;
        }).filter(i => !!i);

        const variantIds = map(variantList, item => {
            if (item) {
                return item.product_id;
            }

            return undefined;
        });

        return { product_list: productList, product_ids: productIds, variant_list: variantList, variant_ids: variantIds };
    }

    /**
     * Sends cart update event
     */
    protected cartUpdateEvent(
        properties: NBloomreach.IBasketItemCartUpdateEventProperties | NBloomreach.IPromoCartUpdateEventProperties
    ): void {
        this.track({
            name: NBloomreach.EEvent.CartUpdate,
            properties
        });
    }

    /**
     * Returns common properties for cart update events
     */
    protected getCartUpdateEventProperties(action: NBloomreach.ECartUpdateActions): NBloomreach.ICartUpdateEventProperties {
        const productList = this.getProductList();

        return {
            action,
            page_type: this.getPageType(),
            product_list: productList,
            product_ids: productList.map(item => item.product_id),
            total_quantity: this.basket.data.qty,
            total_price: this.basket.data.totals?.grand_total || 0,
            total_price_local_currency: this.basket.data.totals?.grand_total || 0
        };
    }

    /**
     * Returns product event properties
     */
    protected getProductEventProperties(product: ProductDecorator<IProductDataV2>): NBloomreach.IViewProductEventProperties | undefined {
        if (product?.data) {
            const orgPrice = product.orgPrice;
            const price = product.price;
            const discount = orgPrice && price && orgPrice > price ? orgPrice - price : 0;
            const stock = product.stock as Stock;

            return {
                ...this.getProductBloomreachEventProperties(product),
                price_local_currency: price,
                discount_value: discount,
                original_price: orgPrice || price,
                original_price_local_currency: orgPrice || price,
                local_currency: this.app.data.currency.base,
                stock_level: stock?.qty,
                ...this.getLocalProductEventProperties(product),
                ...this.getGeneralProperties(),
                page_type: this.getPageType()
            };
        }

        return undefined;
    }

    protected getLocalProductEventProperties(product: ProductDecorator<IProductDataV2>): NBloomreach.IProductData {
        const productData: NBloomreach.IProductData = {
            categories_ids: product.data?.category_ids && map(product.data.category_ids, categoryId => categoryId.toString()),
            product_id: product.parent?.data?.sku || product.id.toString(),
            categories_path: this.getProductCategoriesPath(product),
            price: Math.round((product.data?.price || 0) * this.getPriceGBPRate() * 100) / 100,
            original_price: Math.round((product.data?.org_price || product.data?.price || 0) * this.getPriceGBPRate() * 100) / 100
        };

        if (product.parent?.data?.sku !== product.data?.sku) {
            productData.variant_id = product.data?.sku;
        }

        const department = optionLabelPipe(product.data?.department, 'department');

        if (typeof department === 'string') {
            productData.category_1 = department;
            productData.department = department;
        }

        const productTypeFilter = optionLabelPipe(product.data?.product_type_filter, 'product_type_filter');

        if (typeof productTypeFilter === 'string') {
            productData.category_2 = productData.product_type_filter = productTypeFilter;
        }

        const style = optionLabelPipe(product.data?.style, 'style');

        if (typeof style === 'string') {
            productData.category_3 = productData.style = style;
        }

        const brand = optionLabelPipe(product.data?.brand, 'brand');

        if (typeof brand === 'string') {
            productData.brand = brand;
        }

        const productGroup = product.data?.productgroup;

        if (productGroup) {
            productData.product_group = productGroup;
        }

        const categoryPath = product.data?.category_path;

        if (categoryPath) {
            productData.categories_path = categoryPath;
        }

        const subGroup = product.data?.subgroup;

        if (subGroup) {
            productData.sub_group = subGroup;
        }

        const typenumber = product.data?.typenumber;

        if (typenumber) {
            productData.typenumber = typenumber;
        }

        const groupnumber = product.data?.groupnumber;

        if (groupnumber) {
            productData.groupnumber = groupnumber;
        }

        productData.category_id = this.getProductCategoryIdFromMeta();
        // discount percentage
        productData.markdown_percentage = Math.round((1 - productData.price / (productData.original_price || productData.price)) * 100);
        productData.markdown_value = (productData.original_price || productData.price) - productData.price;

        productData.sale_category = this.isInSaleCategory(product);

        return productData;
    }

    protected getPriceGBPRate(): number {
        const currentCurrency = find(this.app.data.currency.list, currency => currency.code === 'GBP');

        if (currentCurrency) {
            return currentCurrency.rate;
        }

        return 1;
    }

    protected isInSaleCategory(product: ProductDecorator<IProductDataV2>): boolean {
        return product.data?.category_ids?.includes(this.app.data?.['saleCategoryId']) || false;
    }

    protected getProductCategoryIdFromMeta(): string | undefined {
        const currentModel = this.meta.model;
        const previousModel = this.meta.previousState?.model;

        if (
            currentModel &&
            currentModel.data &&
            currentModel.type === 'product' &&
            previousModel &&
            previousModel.data &&
            previousModel.type === 'category'
        ) {
            const product = currentModel as ProductDecorator<IProductDataV3>;
            const category = previousModel as Category;

            if (product.data?.category_ids && product.data?.category_ids.indexOf(category.id)) {
                return category.id.toString();
            }
        }

        return undefined;
    }

    protected getGeneralProperties(): NBloomreach.IGeneralData {
        const store = this.app.multisite.current;
        const langs = store?.lang?.filter(lang => lang !== 'x-default');

        return {
            local_currency: this.app.data.currency.base,
            language: langs?.[0].substring(0, 2),
            domain: store?.domain
        };
    }

    /**
     * Returns product Bloomreach event properties
     */
    protected getProductBloomreachEventProperties(product: Product<IProductDataV2>): NBloomreach.IProductEventProperties {
        function getCategoryBreadcrumbs(breadcrumbs?: ICrumb[]): { category_1: string; category_2: string; category_3: string } {
            const categoryBreadcrumbs = { category_1: '', category_2: '', category_3: '' };

            if (breadcrumbs) {
                take(take(breadcrumbs, breadcrumbs.length - 1), 3).forEach((breadcrumb, index) => {
                    categoryBreadcrumbs[`category_${index + 1}`] = breadcrumb.label;
                });
            }

            return categoryBreadcrumbs;
        }

        const categoryId = product.data?.categories?.last;

        return {
            product_id: `${product.data?.id}`,
            title: `${product.data?.name || ''}`.trim(),
            ...getCategoryBreadcrumbs(product.data?.breadcrumbs),
            categories_path: this.getProductCategoriesPath(product),
            category_id: categoryId ? categoryId.toString() : '',
            categories_ids: this.getCategoryRootPath(product)
        } as NBloomreach.IProductEventProperties;
    }

    /**
     * Returns categories path for product
     */
    protected getProductCategoriesPath(product: Product<IProductDataV2>): string {
        const breadcrumbs = product.data?.breadcrumbs;

        if (breadcrumbs) {
            return this.getCategoriesPath(take(breadcrumbs, breadcrumbs.length - 1));
        }

        return '';
    }

    /**
     * Returns categories path
     */
    protected getCategoriesPath(breadcrumbs: ICrumb[]): string {
        return breadcrumbs.map(breadcrumb => breadcrumb.label).join('>');
    }

    /**
     * Returns category root path
     */
    protected getCategoryRootPath(model: Category<ICategoryDataV2> | Product<IProductDataV2>): string[] {
        const categoryRootPath = model.data?.category_root_path;

        if (categoryRootPath) {
            return categoryRootPath.map(id => id.toString());
        }

        return [];
    }

    /**
     * Returns basket product list
     */
    protected getProductList(basketData = this.basket.data): NBloomreach.ICartProductListItem[] {
        return basketData.items.map(item => {
            return {
                product_id: item.product.toString(),
                quantity: item.request.qty
            };
        });
    }

    /**
     * Returns page type
     */
    protected getPageType(): string | undefined {
        const model = this.meta.model;

        if (model && model.data) {
            switch (model.data.type) {
                case 'product':
                case 'category':
                    return model.data.type;
                case 'page':
                    return (model as IPage<IPageDataV3>).data?.name;
            }
        }

        const url = this.router.url.toLowerCase();

        switch (true) {
            case startsWith(url, routeOverride('/account')):
                return 'account';
            case startsWith(url, routeOverride('/checkout')):
                return 'checkout';
            case startsWith(url, routeOverride('/basket')):
                return 'basket';
            default:
                return undefined;
        }
    }

    /**
     * Sends search event
     */
    protected searchEvent(search?: SearchResult): void {
        if (search instanceof SearchResult) {
            const count = search.data?.count?.product;

            const properties: NBloomreach.ISearchEventProperties = {
                search_term: search.term || '',
                number_of_search_results: count || 0
            };

            this.track({ name: NBloomreach.EEvent.Search, properties });
        }
    }

    onNewsletterConsentChange(value: any, email: string, placement?: string, message?: string) {
        if (value !== undefined) {
            const currentStore = this.app.multisite.current;
            const action = this.getNewsletterAction(value);
            const eventProperties: NBloomreach.INewsletterConsent = {
                capture: 'Magento',
                store_id: currentStore?.id,
                action,
                category: 'newsletter',
                timestamp: Date.now(),
                identification_type: 'email',
                identification: email,
                source: 'public_api',
                placement: placement || 'public_api',
                valid_until: 'unlimited',
                message: message || '',
                current_website: currentStore?.name,
                ...this.getGeneralProperties()
            };

            const date = this.datePipe?.transform(new Date(), 'dd/MM/yy');

            if (value) {
                eventProperties.optin_date = date;
            } else {
                eventProperties.optout_date = date;
            }

            if (placement === 'footer_GDPR' || placement === 'subscription_module' || placement === 'backinstock') {
                this.identifyCustomer(email);
            }

            this.newsletterConsentEvent(eventProperties);
        }
    }

    protected getNewsletterAction(value: unknown): NBloomreach.ENewsletterConsentActions {
        const { Accept, Reject } = NBloomreach.ENewsletterConsentActions;

        return value ? Accept : Reject;
    }

    /**
     * Sends newsletter consent event
     */
    protected newsletterConsentEvent(properties: NBloomreach.INewsletterConsent): void {
        // block newsletter consent event
        this.trackBackend({
            name: NBloomreach.EEvent.NewsletterConsent,
            email: properties.identification,
            properties
        });
    }

    /**
     * On wishlist add/remove callback
     */
    protected onWishlistChange(action: NBloomreach.EWishlistActions, ids: number[]): void {
        ids.forEach(id => {
            const product = this.app.resources.product.getExisting(id) as ProductDecorator<IProductDataV2>;

            if (product) {
                const eventProperties = this.getProductEventProperties(product);

                if (eventProperties) {
                    delete eventProperties.stock_level;

                    this.wishlistChangeEvent({ ...eventProperties, action } as NBloomreach.IWishlistChangeEventProperties);
                }
            }
        });
    }

    /**
     * Sends wishlist change event
     */
    protected wishlistChangeEvent(properties: NBloomreach.IWishlistChangeEventProperties): void {
        this.track({ name: NBloomreach.EEvent.Wishlist, properties });
    }

    /**
     * On checkout step change callback
     */
    protected onCheckoutStepChange(checkout: CheckoutFormComponent, stepBeforeName: string): void {
        const eventProperties = this.getCheckoutStepEventProperties(checkout, stepBeforeName);

        if (eventProperties) {
            this.checkoutStepChangeEvent(eventProperties);
        }
    }

    protected onCheckoutSubmitBefore(checkoutSubmitBeforeData: Checkout.ISubmitBeforeEventData): void {
        this.checkoutBeforeSubmitData = {
            submissionData: cloneDeep(checkoutSubmitBeforeData.data),
            customerData: cloneDeep(this.customer.data),
            basketData: cloneDeep(this.basket.data),
            checkoutBackendData: cloneDeep((<any>checkoutSubmitBeforeData.checkout).backendData),
            checkoutData: cloneDeep((<any>checkoutSubmitBeforeData.checkout).data)
        };
    }

    protected onCheckoutError(): void {
        this.checkoutBeforeSubmitData = undefined;
    }

    /**
     * On checkout success callback
     */
    protected onCheckoutSuccess(successData: Checkout.ISuccessData): void {
        if (this.checkoutBeforeSubmitData) {
            const checkoutBeforeSubmitData = cloneDeep(this.checkoutBeforeSubmitData);
            this.checkoutBeforeSubmitData = undefined;

            this.app.apiConfigService
                .loadCountries()
                .then(() => {
                    this.onCheckoutCompletedStep(checkoutBeforeSubmitData);
                    this.onPurchase(checkoutBeforeSubmitData, successData);
                })
                .catch(() => {});

            this.onCheckoutSuccessCallback(successData, checkoutBeforeSubmitData);
        }
    }

    protected onCheckoutCompletedStep(data: NBloomreach.ICheckoutSubmitData): void {
        const { basketData, submissionData, checkoutData, checkoutBackendData } = data;
        const promoCodes = basketData.coupon;
        const paymentMethodCode = submissionData.paymentMethod.method;
        const paymentMethod = paymentMethodCode
            ? find(checkoutBackendData.payment.methods, method => method.code === paymentMethodCode)
            : undefined;
        const productList = this.getProductList(basketData);

        const eventProperties: NBloomreach.ICheckoutStepChangeEventProperties = {
            step_title: 'Completed',
            promo_code: Array.isArray(promoCodes) ? promoCodes[0] : promoCodes,
            promo_total_value: basketData.coupon_value || 0,
            payment_type: this.parsePaymentMethodTitle(paymentMethod?.['title'], paymentMethod?.['code']),
            delivery_type: checkoutData.shipping.method,
            delivery_cost: basketData.totals?.shipping,
            delivery_country: checkoutData.address.shipping.country || this.getCountryByCode(checkoutData.address.shipping.country_id),
            delivery_city: checkoutData.address.shipping.city,
            tax_value: basketData.totals && basketData.totals.tax,
            local_currency: this.app.data.currency.base,
            product_list: productList,
            product_ids: productList.map(item => item.product_id),
            total_quantity: basketData.qty,
            total_price: basketData.totals?.grand_total_global || basketData.totals?.grand_total || 0,
            total_price_without_tax: this.getTotalWithoutTax(basketData),
            total_price_local_currency: basketData.totals?.grand_total || 0
        };

        this.checkoutStepChangeEvent(eventProperties);
    }

    protected onCheckoutSuccessCallback(
        successData: Checkout.ISuccessData,
        checkoutBeforeSubmitData: NBloomreach.ICheckoutSubmitData
    ): void {
        const { checkoutData } = checkoutBeforeSubmitData;

        if (checkoutData.newsletter && !this.sessionStorageService.get('exponeaConsent')) {
            if (!this.sessionStorageService.get('expressPayment')) {
                this.onNewsletterConsentChange(checkoutData.newsletter, successData.customer.email, 'checkout_start_softoptin');
            }

            this.identifyCustomerOnCheckoutSuccess(successData);

            this.updateCustomerData(this.getCustomerDataFromCheckoutData(checkoutBeforeSubmitData, successData));
        }
    }

    protected onPurchase(checkoutBeforeSubmitData: NBloomreach.ICheckoutSubmitData, successData: Checkout.ISuccessData) {
        const items = checkoutBeforeSubmitData.basketData.items;
        const ids = items.map(item => item.product);
        const shippingAdditionalData = checkoutBeforeSubmitData.checkoutData.shipping.additionalData;
        const storeId = shippingAdditionalData?.store_id;

        const promises: Promise<any>[] = [
            this.app.resources.product.loadAllIds(ids, 3, { safe: true }),
            this.app.apiConfigService.loadCountries()
        ];

        if (storeId) {
            promises.push(this.app.resources.store.load(storeId, 1));
        }

        Promise.all(promises).finally(() => {
            items.forEach(item => {
                const basketItem = new BasketItem(item, this.basket);
                const eventProperties = this.getPurchaseItemEventProperties(basketItem, successData, checkoutBeforeSubmitData.basketData);

                if (eventProperties) {
                    this.purchaseItemEvent(eventProperties);
                }
            });

            if (this.app.apiConfigService.countries) {
                this.purchaseEvent(this.getPurchaseEventProperties(checkoutBeforeSubmitData, successData));
            }
        });
    }

    protected getPurchaseEventProperties(
        checkoutSubmitData: NBloomreach.ICheckoutSubmitData,
        successData: Checkout.ISuccessData
    ): NBloomreach.IPurchaseEventProperties {
        const { basketData, submissionData, checkoutData } = checkoutSubmitData;
        const promoCodes = basketData.coupon;

        const productList = this.getProductList(basketData);
        const eventProperties: NBloomreach.IPurchaseEventProperties = {
            purchase_id: successData.number || successData.numbers?.[0],
            purchase_status: successData.status,
            promo_code: Array.isArray(promoCodes) ? promoCodes[0] : promoCodes,
            promo_value: basketData.coupon_value || 0,
            payment_type: this.parsePaymentMethod(submissionData.paymentMethod.method?.toString()),
            delivery_type: checkoutData.shipping.method,
            delivery_cost: basketData.totals?.shipping || 0,
            delivery_country: this.getCountryByCode(checkoutData.address.shipping?.country_id),
            delivery_city: checkoutData.address.shipping && checkoutData.address.shipping.city,
            tax_value: basketData.totals?.tax || 0,
            local_currency: this.app.data.currency.base,
            product_list: productList,
            product_ids: productList.map(product => product.product_id),
            total_quantity: basketData.qty,
            total_price: basketData.totals?.grand_total_global || basketData.totals?.grand_total || 0,
            total_price_without_tax: this.getTotalWithoutTax(basketData),
            total_price_local_currency: basketData.totals?.grand_total || 0
        };

        return eventProperties;
    }

    protected parsePaymentMethod(code?: string): string | undefined {
        if (this.sessionStorageService.get('expressPayment') && this.expressPaymentMethods.indexOf(`${code}`) !== -1) {
            return `${code}_express`;
        }

        return code;
    }

    protected getCheckoutStepEventProperties(
        checkout: CheckoutFormComponent,
        stepBeforeName: string
    ): NBloomreach.ICheckoutStepChangeEventProperties | undefined {
        const stepBefore = checkout.getStepByName(stepBeforeName);

        if (stepBefore && checkout.currentStep) {
            const stepBeforeOrder = stepBefore.order;
            const currentStepOrder = checkout.currentStep.order;

            // we want to track completeness of steps
            if (stepBeforeOrder && currentStepOrder && currentStepOrder >= stepBeforeOrder) {
                const paymentMethod = checkout.data.payment.method ? checkout.paymentMethodByCode(checkout.data.payment.method) : undefined;
                const promoCodes = this.basket.data.coupon;
                const totalPrice = this.basket.data.totals?.grand_total_global || this.basket.data.totals?.grand_total || 0;

                const eventProperties: NBloomreach.ICheckoutStepChangeEventProperties = {
                    step_title: stepBefore.stepTitle,
                    step_number: findIndex(checkout.stepsSequence, item => item.name === stepBefore.name) + 1,
                    promo_code: Array.isArray(promoCodes) ? promoCodes[0] : promoCodes,
                    promo_total_value: this.basket.data.coupon_value || 0,
                    payment_type: this.parsePaymentMethodTitle(paymentMethod?.['title'], paymentMethod?.['code']),
                    delivery_type: checkout.data.shipping.method,
                    delivery_cost: this.basket.data.totals?.shipping,
                    delivery_country:
                        checkout.data.address.shipping.country || this.getCountryByCode(checkout.data.address.shipping.country_id),
                    delivery_city: checkout.data.address.shipping?.city,
                    tax_value: this.basket.data.totals && this.basket.data.totals.tax,
                    local_currency: this.app.data.currency.base,
                    total_quantity: this.basket.data.qty,
                    total_price: Math.round(totalPrice * this.getPriceGBPRate() * 100) / 100,
                    total_price_without_tax: this.getTotalWithoutTax(),
                    total_price_local_currency: this.basket.data.totals?.grand_total || 0,
                    ...this.getGeneralProperties(),
                    ...this.getProductAndVariantLists()
                };

                return eventProperties;
            }
        }

        return undefined;
    }

    /**
     * Returns Bloomreach customer update data from checkout success data
     */
    protected getCustomerDataFromCheckoutData(
        checkoutData: NBloomreach.ICheckoutSubmitData,
        successData: Checkout.ISuccessData
    ): NBloomreach.ICustomerUpdate {
        const { submissionData } = checkoutData;
        const address = submissionData.shipping_address || submissionData.billing_address;
        const customerData: NBloomreach.ICustomerUpdate = {
            email: successData.customer.email
        };

        if (address) {
            customerData.first_name = address.firstname;
            customerData.last_name = address.lastname;
            customerData.title = address.title;
            customerData.city = address.city;
            customerData.country = this.getCountryByCode(address.country_id);
            customerData.phone = address.telephone;
        }

        return customerData;
    }

    protected getPurchaseItemEventProperties(
        basketItem: BasketItem,
        successData: Checkout.ISuccessData,
        basketData: IBasketData
    ): NBloomreach.IPurchaseItemEventProperties | undefined {
        const product = basketItem.product;
        if (product?.data) {
            const productProperties = this.getProductBloomreachEventProperties(product);
            delete productProperties.category_id;
            delete productProperties.category_2;
            delete productProperties.category_3;

            const price = basketItem.price;
            const orgPrice = basketItem.orgPrice;
            const discount = orgPrice && price && orgPrice > price ? orgPrice - price : 0;

            const purchaseId = successData.number || successData.numbers?.[0];

            const eventProperties: NBloomreach.IPurchaseItemEventProperties = {
                ...productProperties,
                purchase_id: purchaseId,
                purchase_status: successData.status,
                price,
                price_local_currency: price,
                discount_value: discount,
                discount_percentage: discount ? (discount / price) * 100 : 0,
                original_price: orgPrice || price,
                original_price_local_currency: orgPrice || price,
                local_currency: this.app.data.currency.base,
                quantity: basketItem.qty,
                total_price: basketData.totals?.grand_total_global || basketData.totals?.grand_total || 0,
                total_price_without_tax: this.getTotalWithoutTax(basketData),
                total_price_local_currency: basketData.totals?.grand_total || 0
            };

            return eventProperties;
        }
    }

    /**
     * Sends checkout step event
     */
    protected checkoutStepChangeEvent(properties: NBloomreach.ICheckoutStepChangeEventProperties): void {
        this.track({ name: NBloomreach.EEvent.Checkout, properties });
    }

    /**
     * Sends purchase item event
     */
    protected purchaseItemEvent(properties: NBloomreach.IPurchaseItemEventProperties): void {
        // block purchase item event
        // this.track({ name: NBloomreach.EEvent.PurchaseItem, properties });
    }

    /**
     * Sends purchase event
     */
    protected purchaseEvent(properties: NBloomreach.IPurchaseEventProperties): void {
        // block purchase event
        // this.track({ name: NBloomreach.EEvent.Purchase, properties });
    }

    /**
     * Parses payment method title
     */
    protected parsePaymentMethodTitle(title?: string, code?: string | number): string {
        if (this.sessionStorageService.get('expressPayment') && this.expressPaymentMethods.indexOf(`${code}`) !== -1) {
            return `${title} Express`;
        }

        return title || '';
    }

    protected getTotalWithoutTax(basketData = this.basket.data): number {
        if (basketData.totals) {
            const { grand_total, tax } = basketData.totals;

            if (tax) {
                return Math.round((grand_total - tax) * 100) / 100;
            }

            return grand_total;
        }

        return 0;
    }

    protected getCountryByCode(code: string | undefined): string | undefined {
        if (!code) {
            return undefined;
        }

        const country =
            this.app.apiConfigService.countries &&
            code &&
            find(this.app.apiConfigService.countries, iCountry => iCountry.code.toLowerCase() === code.toLowerCase());

        return country && country.name;
    }
    /**
     * On register callback
     */
    protected onRegister(): void {
        // reset core
        return;

        // this.onNewsletterConsentChange(this.customer.data.newsletter, this.customer.data.email as string, 'register');
        // this.updateCustomerData();
        // this.identifyCustomer();
    }

    protected onLogin(): void {
        this.identifyCustomer();
    }

    protected updateCustomerData(customerData = this.getCustomerData()): void {
        if (typeof window === 'undefined') {
            return;
        }

        if (window.exponea) {
            this.app.apiConfigService
                .loadCountries()
                .then(() => {
                    window.exponea.update(customerData);
                })
                .catch(() => {});
        }
    }

    /**
     * Returns Bloomreach customer update data from customer data
     */
    protected getCustomerData(): NBloomreach.ICustomerUpdate {
        const customerData: NBloomreach.ICustomerUpdate = {
            first_name: this.customer.data.firstname,
            last_name: this.customer.data.lastname,
            email: this.app.customer.model.data?.email,
            title: this.customer.data.prefix,
            gender: this.customer.data.gender
        };

        const dob = toDate(this.customer.data.dob);

        if (dob) {
            // We take seconds and add 6 hours to avoid summer time shifting the date to previous day.
            customerData.birth_date = dob.getTime() / 1000 + 3600;
        }

        const defaultAddress = this.app.customer.addressBook?.shipping;

        if (defaultAddress) {
            customerData.city = defaultAddress.data.city;
            customerData.country = this.getCountryByCode(defaultAddress.data.country_id);
            customerData.phone = defaultAddress.data.telephone;
        }

        return customerData;
    }

    /**
     * Calls identify method
     */
    identifyCustomer(
        identifier: number | string | undefined = this.app.customer.model.data?.email,
        id: number | string | undefined = this.app.customer.model.data?.id,
        watchdogEmail?: string,
        customProperties?: Record<string, unknown>
    ) {
        if (!this.initiated) {
            return;
        }

        const idString = (id && id.toString()) || undefined;

        if (this.window.exponea && identifier) {
            const customerId: NBloomreach.ICustomerIdentify = { registered: identifier.toString(), magento_customer_id: idString };

            this.window.exponea.identify(customerId, customProperties);

            if (watchdogEmail) {
                // do not send the watchdog_email in identify() call!
                this.window.exponea.update({
                    watchdog_email: watchdogEmail
                });
            }
        }
    }

    protected identifyCustomerOnCheckoutSuccess(successData: Checkout.ISuccessData) {
        this.identifyCustomer(successData.customer.email, successData.customer.id);
    }

    /**
     * On basket data update callback
     */
    protected onBasketUpdate(): void {
        const coupons = this.basket.data.coupon;
        const promo: NBloomreach.ICurrentPromo = {
            code: Array.isArray(coupons) ? coupons[0] : coupons,
            value: this.basket.data.coupon_value || 0
        };

        if (this.promo.code === null) {
            this._promo = promo;
        } else {
            this.promo = promo;
        }
    }

    /**
     * On promo coupon change callback
     */
    protected onPromoChange(action: NBloomreach.TPromoCartUpdateAction, promoCode: string, promoValue: number): void {
        const eventProperties: NBloomreach.IPromoCartUpdateEventProperties = {
            ...this.getCartUpdateEventProperties(action),
            promo_code: promoCode,
            promo_value: promoValue
        };

        this.cartUpdateEvent(eventProperties);
    }

    protected onAuthentication(): void {
        const loggedIn = this.customer.isLoggedIn;
        const { id, email } = this.customer.data;
        const eventProperties: NBloomreach.ICustomerAuthenticationEventProperties = {
            logged_in: loggedIn,
            user_id: loggedIn ? id && id.toString() : undefined,
            email: loggedIn ? email : undefined
        };

        this.authenticationEvent(eventProperties);

        this.timeoutService.setTimeout(() => this.identifyCustomer(), 20);
    }

    /**
     * Sends authentication event
     */
    protected authenticationEvent(properties: NBloomreach.ICustomerAuthenticationEventProperties): void {
        this.track({ name: NBloomreach.EEvent.Authentication, properties });
    }

    protected onMetaChange() {
        const path = this.meta.getMetaProperty('canonical') || this.meta.getMetaProperty('url') || this.window.location.pathname;

        if (this.lastPath !== path) {
            this.lastPath = path;
            const model = this.meta.model;

            if (model && model.type === 'category') {
                this.viewCategoryEvent(model as Category<ICategoryDataV3>);
            }
        }
    }

    /**
     * Sends product view event
     */
    protected viewProductEvent(product: ProductDecorator<IProductDataV3>): void {
        if (product?.data) {
            const properties = this.getProductEventProperties(product);

            if (properties) {
                this.track({ name: NBloomreach.EEvent.ViewProduct, properties });
            }
        }
    }

    /**
     * Sends category view event
     * @param {Category} category
     */
    protected viewCategoryEvent(category: Category<ICategoryDataV3>): void {
        if (category?.data) {
            const properties = this.getViewCategoryEventProperties(category);
            this.track({ name: NBloomreach.EEvent.ViewCategory, properties });
        }
    }

    /**
     * Returns view category event properties
     */
    protected getViewCategoryEventProperties(category: Category<ICategoryDataV3>): NBloomreach.IViewCategoryEventProperties {
        const breadcrumbs = category.data!.breadcrumbs;
        const listedProducts = category.data!.products ? category.data!.products.map(id => id.toString()) : [];

        return {
            category_id: category.data!.id.toString(),
            category_name: category.data!.name,
            category_1: breadcrumbs && breadcrumbs.length ? breadcrumbs[0].label : '',
            categories_path: this.getCategoryCategoriesPath(category),
            categories_ids: this.getCategoryRootPath(category),
            local_currency: this.app.data.currency.base,
            listed_products: listedProducts,
            ...this.getGeneralProperties()
        };
    }

    /**
     * Returns categories path for category
     */
    protected getCategoryCategoriesPath(category: Category<ICategoryDataV2>): string {
        const breadcrumbs = category.data?.breadcrumbs;

        if (breadcrumbs) {
            return this.getCategoriesPath(breadcrumbs);
        }

        return '';
    }

    protected onLoginOrRegisterRequest(newsletter: boolean, placement?: string) {
        this.updateCustomerData();
        this.identifyCustomer(this.app.customer.model.data?.email);
        this.onNewsletterConsentChange(newsletter, this.app.customer.model.data?.email as string, placement);
        // We do not follow usual subscription path, we delete newsletter property from login/register data
        // Because of it, core gtm service will not trigger newsletter event so we need to trigger it here.
        this.gtmService.trackNewsletter(newsletter, this.app.customer.model.data.email, placement);
    }

    checkConsent(status: Status | undefined, email: string, onSuccess?: IRequestCallback, onError?: IRequestCallback): void {
        this.requestService.send(
            status,
            {
                path: 'exponea/consent',
                data: { email }
            },
            onSuccess,
            onError
        );
    }

    loadRecommendationsProducts(options: string | NBloomreach.IRecommendationsOptions, productSku?: string[]): Promise<Product[]> {
        return new Promise((resolve, reject) => {
            this.loadRecommendationsProductIds(options, productSku)
                .then(ids => {
                    if (ids?.length) {
                        this.app.resources.product
                            .loadAllIds(ids, 2, { safe: true })
                            .then(() => {
                                resolve(this.app.resources.product.getAllExisting(ids));
                            })
                            .catch(reject);
                    } else {
                        resolve([]);
                    }
                })
                .catch(reject);
        });
    }

    loadRecommendationsProductIds(options: string | NBloomreach.IRecommendationsOptions, productSku?: string[]): Promise<number[]> {
        return new Promise((resolve, reject) => {
            this.loadRecommendations(options, productSku)
                .then(async items => {
                    const skus = items?.length ? compact(items.map(item => item.item_id)) : [];

                    if (skus.length) {
                        try {
                            await this.app.resources.product.find({ verbosity: 2, filter: { sku: { $in: skus } } });

                            resolve(
                                this.app.resources.product
                                    .getAllExisting()
                                    .filter(i => i.data?.sku && skus.includes(i.data.sku))
                                    .map(i => i.id)
                            );
                        } catch (error) {
                            reject(error);
                        }
                    } else {
                        resolve([]);
                    }
                })
                .catch(reject);
        });
    }

    /**
     * Parses snippet from Bloomreach admin panel to get recommendations options
     */
    parseRecommendationsSnippet(snippetEntered: string): NBloomreach.IRecommendationsOptions | undefined {
        // we don't know what exactly comes in
        try {
            let snippet: string | string[] = snippetEntered;
            const optionsIndex = snippet.indexOf('var options');
            // we remove part of snippet before var options
            snippet = optionsIndex !== -1 ? snippet.substr(optionsIndex) : snippet;
            const recommendationIndex = snippet.indexOf('exponea.getRecommendation');
            // we remove part of snippet after options
            snippet = recommendationIndex !== -1 ? snippet.substr(0, recommendationIndex) : snippet;
            snippet = snippet.trim();
            const bracketIndex = snippet.indexOf('{');
            // if there is no open bracket string is invalid
            if (bracketIndex === -1) {
                return undefined;
            }
            // snippet = snippet.substr(bracketIndex);
            // we check close brakcet
            snippet = snippet.split('').reverse();
            const endBracketIndex = snippet.indexOf('}');
            // if no close bracket string is invalid
            if (endBracketIndex === -1) {
                return undefined;
            }
            // we remove what is after close bracket
            snippet = snippet.slice(endBracketIndex);
            snippet = snippet.reverse().join('');

            let options;
            snippet = snippet.replace('onRecommendationsLoaded', '""').replace('var', '');
            eval(snippet);
            if (options) {
                return options as NBloomreach.IRecommendationsOptions;
            } else {
                return undefined;
            }
        } catch (e) {
            this.app.logger.error(e);
            return undefined;
        }
    }

    protected loadRecommendations(
        options: string | NBloomreach.IRecommendationsOptions,
        productSku?: string[]
    ): Promise<NBloomreach.IRecommendationsItem[]> {
        return new Promise((resolve, reject) => {
            const recommendationsOptions = this.getRecommendationsOptions(options, productSku);

            if (recommendationsOptions) {
                const promise = this.getRecommendations(recommendationsOptions);
                if (promise) {
                    promise.then(resolve).catch(reject);
                } else {
                    reject();
                }
            } else {
                reject();
            }
        });
    }

    /**
     *  Returns recommendations options
     */
    private getRecommendationsOptions(
        options: string | NBloomreach.IRecommendationsOptions,
        productSku?: string[]
    ): NBloomreach.IRecommendationsOptions | undefined {
        if (options) {
            const recommendationsOptions = typeof options === 'string' ? this.parseRecommendationsSnippet(options) : options;

            if (recommendationsOptions) {
                if (productSku?.length) {
                    recommendationsOptions.items = {};

                    productSku.forEach(sku => {
                        recommendationsOptions.items[sku] = 1;
                    });
                }

                return recommendationsOptions;
            }
        }

        return undefined;
    }

    /**
     * Loads recommendations
     */
    private getRecommendations(
        options: NBloomreach.IRecommendationsOptions
    ): Promise<NBloomreach.IRecommendationsItem[] | null> | undefined {
        if (typeof window === 'undefined') {
            return undefined;
        }

        return new Promise((resolve, reject) => {
            if (window.exponea) {
                options = { ...options };
                options.callback = resolve;

                try {
                    window.exponea.getRecommendation(options);
                } catch (e) {
                    this.app.logger.error(e, options);
                    reject();
                }
            } else {
                reject();
            }
        });
    }

    sendBackInStockEvent(variant_id: string, email = this.app.customer.model.data?.email): void {
        const properties: NBloomreach.IStockNotificationEventProperties = {
            variant_id,
            store_id: this.app.multisite.current?.country
        };

        this.trackBackend({
            name: NBloomreach.EEvent.StockNotification,
            email,
            properties
        });
    }

    private getOneTrustActiveGroupsCodes(): string {
        if (typeof window === 'undefined') {
            return '';
        }

        const MAP: Record<string, string> = {
            C0001: 'C0001 Strictly Necessary Cookies',
            C0002: 'C0002 Performance & Analytical Cookies',
            C0003: 'C0003 Functional Cookies',
            C0004: 'C0004 Targeting Cookies',
            C0005: 'C0005 Social Media Cookies'
        };

        const active = (window as any).OnetrustActiveGroups as string | undefined;
        const codes = new Set<string>(
            active
                ? active
                      .split(',')
                      .map(s => s.trim())
                      .filter(Boolean)
                : []
        );

        // Always include Strictly Necessary
        codes.add('C0001');

        return Array.from(codes)
            .map(code => MAP[code])
            .filter(Boolean)
            .join(', ');
    }

    private onPrivacySettingsChange() {
        // Sync cookie consent categories with Bloomreach
        try {
            if (window.exponea) {
                const data: {
                    customer_consent_preferences: string;
                } = {
                    customer_consent_preferences: this.getOneTrustActiveGroupsCodes()
                };

                this.app.logger.log('Bloomreach', 'Updating customer consent preferences', data);

                window.exponea.update(data);
            }
        } catch (error) {
            this.app.logger.error('Bloomreach', 'Error updating customer consent preferences', error);
        }
    }
}
